/**
 * How a Dining Profile entry is SHOWN — decided once, for every surface that
 * shows one: a restaurant's My Notes, the notes pop-up, the Favorites row and
 * the chat's "Add this to one of them…" list (2026-09-23).
 *
 * Each surface used to make these calls itself, and the day's display bugs
 * were one surface not knowing what another had learned: RM's transcription of
 * a card printed as though the member wrote it, saved people missing from one
 * list and shown in another, a status that read differently in two places. The
 * words and the status come from here; each surface keeps its own actions.
 *
 * One note, one text (Kapil, 2026-09-24: "it can only be a private note or has
 * to be a highlight… it can't be both"). A note that is shared IS the
 * Highlight: what others read is the note's own words, so a surface shows one
 * text and one status per note, never a second version.
 */
import { noteShareStatus } from '@/utils/profileNotes'
import { relativeAgo } from '@/utils/relativeAgo'

export const isContactEntry = entry => !!entry && entry.kind === 'contact'

// A card sent with no words holds RM's transcription of the card, never shown
// as the member's own note — the contact line already says it.
export const isCardTranscription = entry => !!(entry && entry.source && entry.source.transcribedFromCard)

/**
 * The member's own words for this entry, exactly as written — '' when it has
 * none. The restaurant's name stays when they wrote it (Kapil 2026-09-29, RM-346).
 */
export function ownWords(entry) {
  if (!entry || isCardTranscription(entry)) return ''

  return entry.text || ''
}

export const activeShares = entry => ((entry && entry.shares) || []).filter(s => s && s.status === 'active')

/**
 * Kapil's words for what a note IS (2026-09-22): "Private Only", "Highlight
 * shared with your Network", "Shared with {Restaurant}". A saved person is
 * never shared, so they are always Private Only.
 */
export function shareLabel(entry, restaurantName) {
  if (isContactEntry(entry)) return 'Private Only'

  return noteShareStatus(activeShares(entry).map(s => s.audience), restaurantName)
}

/** "Contact · Private Only" for a person; the share label for a note. */
export function statusLine(entry, restaurantName) {
  return isContactEntry(entry) ? 'Contact · Private Only' : shareLabel(entry, restaurantName)
}

/** When the member last did something with it — wrote, edited or shared it. */
export function entryAgo(entry) {
  const at = entry && (entry.activityAt || entry.editedAt || entry.capturedAt)

  return at ? `${relativeAgo(at)} ago` : ''
}

/**
 * Whether this note has ever gone to its restaurant. A restaurant hears from a
 * member ONCE (Kapil, 2026-09-23: "once a member shares a highlight once with
 * a restaurant, we can't have them send again").
 */
export const sentToRestaurant = entry => ((entry && entry.shares) || [])
  .some(s => s && s.audience === 'restaurant' && (s.status === 'active' || s.status === 'withdrawn'))

/**
 * Whether this note can still go to its restaurant: it is a Favorite, and the
 * restaurant has not heard from the member before — once per RESTAURANT, which
 * the server says in `restaurant.alreadyShared`.
 */
export const restaurantShareOpen = entry => !!(entry && entry.restaurant && entry.restaurant.isFavorite)
  && !(entry.restaurant && entry.restaurant.alreadyShared)
  && !sentToRestaurant(entry)

/** Whether their Network sees this note — a note shared with it IS the Highlight. */
export const onNetwork = entry => activeShares(entry).some(s => s.audience === 'network' || s.audience === 'public')

/** When the member last did something with it — wrote, edited or shared it. */
export const entryAt = entry => (entry && (entry.activityAt || entry.editedAt || entry.capturedAt)) || null

/**
 * The one question before an entry is removed — the same on My Notes and in
 * the notes pop-up. Removing is irreversible from either, so it is always
 * asked, and a shared note says its share goes with it.
 */
export function removeQuestion(entry) {
  if (isContactEntry(entry)) return 'Remove this contact? Their card goes with them.'

  return activeShares(entry).length
    ? 'Remove this note? It has been shared, and removing it withdraws that share too.'
    : 'Remove this note?'
}
